import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { 
  ClipboardCheck, 
  Share2, 
  FileText, 
  DollarSign,
  CheckCircle2,
  AlertCircle,
  ArrowRight
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const stagger = {
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } }
};

const steps = [
  {
    icon: ClipboardCheck,
    step: '1',
    title: 'Apply to join',
    description: 'Approval required',
    testId: 'step-apply'
  },
  {
    icon: Share2,
    step: '2',
    title: 'Share your unique referral link',
    description: 'Send to friends, family & network',
    testId: 'step-share'
  },
  {
    icon: FileText,
    step: '3',
    title: 'Your referral signs up',
    description: 'For an Individual Tax Return (Form 1040)',
    testId: 'step-signup'
  },
  {
    icon: DollarSign,
    step: '4',
    title: 'When they pay, you earn',
    description: '50% of the prep fee up to $250',
    testId: 'step-earn'
  }
];

const details = [
  'Applies only to new 1040 clients this tax season',
  'Payout is earned only after the client pays in full',
  'No spamming, misleading claims, or self-referrals'
];

// Get the correct apply URL based on environment
const getApplyUrl = () => {
  const hostname = window.location.hostname;
  
  // Development/Preview environments
  if (hostname === 'localhost' || 
      hostname.includes('preview.emergentagent.com') ||
      hostname.includes('127.0.0.1')) {
    return 'https://lead-export-board.preview.emergentagent.com/contractor/apply';
  }
  
  // Production
  return 'https://app.kgob.com/contractor/apply';
};

export default function ICApplication() {
  const handleApply = () => {
    window.open(getApplyUrl(), '_blank');
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      data-testid="ic-application-page"
    >
      <Helmet>
        <title>Independent Contractor Referral Program | KGOB CPAs & Advisors</title>
        <meta name="description" content="Earn up to $250 per new tax return referral. Join KGOB's approved Independent Contractor referral partner program and start earning today." />
        <link rel="canonical" href="https://kgob.com/cash" />
        <meta property="og:url" content="https://kgob.com/cash" />
      </Helmet>
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="flex-1 bg-[linear-gradient(180deg,#0B2240_0%,#07182F_100%)]">
        
        {/* Hero Section */}
        <section className="relative overflow-hidden py-16 lg:py-24">
          {/* Background decoration */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-[#D4AF37]/5 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-[#1FA3A3]/5 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="text-center max-w-3xl mx-auto"
            >
              {/* Badge */}
              <motion.div
                variants={fadeUp}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6"
              >
                <DollarSign className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-[#E3C96E] text-sm font-medium">Independent Contractor Referral Program</span>
              </motion.div>

              {/* Headline */}
              <motion.h1
                variants={fadeUp}
                className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight"
                data-testid="ic-app-headline"
              >
                Earn up to{' '}
                <span className="text-[#D4AF37]">$250</span>
                {' '}per new tax return referral
              </motion.h1>

              {/* Subhead */}
              <motion.p
                variants={fadeUp}
                className="text-white/70 text-lg sm:text-xl mb-10 max-w-2xl mx-auto"
                data-testid="ic-app-subhead"
              >
                Join our approved referral partner program and get paid when your referrals become new tax clients.
              </motion.p>

              {/* CTA Button */}
              <motion.div variants={fadeUp}>
                <Button
                  onClick={handleApply}
                  className="h-14 px-10 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold text-lg shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-[0.98] focus-visible:ring-2 focus-visible:ring-[#1FA3A3] transition-all"
                  data-testid="ic-app-hero-cta"
                >
                  <span>Apply to Join</span>
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <h2 
                className="font-['Playfair_Display'] text-3xl sm:text-4xl text-white mb-4"
                data-testid="how-it-works-heading"
              >
                How it works
              </h2>
              <div className="w-16 h-1 bg-[#D4AF37] mx-auto rounded-full" />
            </motion.div>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.testId}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative group"
                    data-testid={step.testId}
                  >
                    {/* Connector line (hidden on mobile and last item) */}
                    {index < steps.length - 1 && (
                      <div className="hidden lg:block absolute top-12 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-[#D4AF37]/40 to-transparent" />
                    )}
                    
                    <div className="relative rounded-2xl p-6 border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all h-full">
                      {/* Step number */}
                      <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center">
                        <span className="text-[#0B2240] font-bold text-sm">{step.step}</span>
                      </div>
                      
                      {/* Icon */}
                      <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4">
                        <Icon className="h-7 w-7 text-[#D4AF37]" />
                      </div>
                      
                      {/* Content */}
                      <h3 className="text-white font-semibold text-lg mb-2">
                        {step.title}
                      </h3>
                      <p className="text-white/60 text-sm">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Important Details Section */}
        <section className="py-16 lg:py-20 border-t border-white/10">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl p-8 border border-white/10 bg-white/5 backdrop-blur-sm"
              data-testid="important-details-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <AlertCircle className="h-6 w-6 text-[#D4AF37]" />
                <h3 className="font-['Playfair_Display'] text-2xl text-white">
                  Important details
                </h3>
              </div>
              
              <ul className="space-y-4">
                {details.map((detail, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-start gap-3"
                    data-testid={`detail-${index + 1}`}
                  >
                    <CheckCircle2 className="h-5 w-5 text-[#1FA3A3] mt-0.5 flex-shrink-0" />
                    <span className="text-white/80">{detail}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Bottom CTA Section */}
        <section className="py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h2 
                className="font-['Playfair_Display'] text-3xl sm:text-4xl text-white mb-4"
                data-testid="bottom-cta-heading"
              >
                Ready to start earning?
              </h2>
              <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">
                Apply today and start sharing your referral link with friends, family, and your network.
              </p>
              <Button
                onClick={handleApply}
                className="h-14 px-10 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold text-lg shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-[0.98] focus-visible:ring-2 focus-visible:ring-[#1FA3A3] transition-all"
                data-testid="ic-app-bottom-cta"
              >
                <span>Apply to Join</span>
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </motion.div>
          </div>
        </section>

      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
